import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	checkFolderName,
	createFolder as createFolderApi,
	deleteFolder,
	downloadFolder,
	downloadFolders,
	moveFolder,
	renameFolder,
} from '@api/portalFiles';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IResponseError } from '@interfaces/errors/apiErrors';
import {
	ICheckFolderNameDTO,
	ICreateFolderDTO,
	IDeleteFolderDTO,
	IDownloadFolderDTO,
	IDownloadFoldersDTO,
	IMoveFolderDTO,
	IRenameFolderDTO,
} from '@interfaces/portal/dto/foldersManagementDTO';
import { IPortalFolder } from '@interfaces/portal/folders';

import { ESnackbarTypes, EUserRoles } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import {
	GET_PORTAL_FOLDERS,
	PORTAL_CHECK_FOLDER_NAME,
	PORTAL_DELETE_FOLDER,
	PORTAL_DOWNLOAD_FOLDER,
	PORTAL_FOLDERS_CREATE,
	PORTAL_MOVE_FOLDER,
	PORTAL_RENAME_FOLDER,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IUseMutatePortalFoldersReturned {
	createFolder: (data: ICreateFolderDTO) => Promise<void>;
	isCreatingFolder: boolean;
	handleCheckFolderName: (data: ICheckFolderNameDTO) => Promise<void>;
	isLoadingCheckFolderName: boolean;
	handleDeleteFolder: (data: IDeleteFolderDTO) => Promise<void>;
	isLoadingDeleteFolder: boolean;
	handleRenameFolder: (data: IRenameFolderDTO) => Promise<void>;
	isLoadingRenameFolder: boolean;
	handleMoveFolder: (data: IMoveFolderDTO) => Promise<void>;
	isLoadingMoveFolder: boolean;
	handleDownloadFolder: (data: IDownloadFolderDTO) => Promise<void>;
	isLoadingDownloadFolder: boolean;
	handleDownloadFolders: (data: IDownloadFoldersDTO) => void;
	isLoadingDownloadFolders: boolean;
}

interface IUseMutatePortalFolders {
	onCreateFolderSuccess?: (folderId: string) => void;
	closeDeleteConfirm?: () => void;
}

export function useMutatePortalFolders({
	onCreateFolderSuccess,
	closeDeleteConfirm,
}: IUseMutatePortalFolders): IUseMutatePortalFoldersReturned {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		mutateAsync: createFolder,
		isLoading: isCreatingFolder,
	} = useMutation<ICreateFolderDTO>({
		mutationFn: createFolderApi,
		mutationKey: [PORTAL_FOLDERS_CREATE],
		onSuccess: (data: IPortalFolder, variables) => {
			if (onCreateFolderSuccess) onCreateFolderSuccess(data.id);

			dispatch(setSnackbar({
				message: variables.parentFolderId
					? responseMessages.createSubfolderSuccess
					: responseMessages.createFolderSuccess,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FOLDERS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: handleCheckFolderName,
		isLoading: isLoadingCheckFolderName,
	} = useMutation<ICheckFolderNameDTO>({
		mutationKey: [PORTAL_CHECK_FOLDER_NAME],
		mutationFn: checkFolderName,
		onError: (err: IResponseError) => {
			if (!err.errors.find((item) => (
				item.code === EErrorCodes.portalFolderNameMustBeUnique
			))) {
				handleErrorFromMessage(err);
			}
		},
	});

	const {
		mutateAsync: handleDeleteFolder,
		isLoading: isLoadingDeleteFolder,
	} = useMutation<IDeleteFolderDTO>({
		mutationKey: [PORTAL_DELETE_FOLDER],
		mutationFn: deleteFolder,
		onSuccess: (_, variables) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: variables.isParentFolder
					? responseMessages.removedFolder
					: responseMessages.removedSubfolder,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FOLDERS]);
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutateAsync: handleRenameFolder,
		isLoading: isLoadingRenameFolder,
	} = useMutation<IRenameFolderDTO>({
		mutationKey: [PORTAL_RENAME_FOLDER],
		mutationFn: renameFolder,
		onSuccess: (_, variables) => {
			dispatch(setSnackbar({
				message: variables.isParentFolder
					? responseMessages.renameFolder
					: responseMessages.renameSubfolder,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FOLDERS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: handleMoveFolder,
		isLoading: isLoadingMoveFolder,
	} = useMutation<IMoveFolderDTO>({
		mutationKey: [PORTAL_MOVE_FOLDER],
		mutationFn: moveFolder,
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		onSuccess: (_, variables) => {
			dispatch(setSnackbar({
				message: variables.isParentFolder
					? responseMessages.moveFolder
					: responseMessages.moveSubfolder,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FOLDERS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: handleDownloadFolder,
		isLoading: isLoadingDownloadFolder,
	} = useMutation<IDownloadFolderDTO>({
		mutationKey: [PORTAL_DOWNLOAD_FOLDER],
		mutationFn: downloadFolder,
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.apiDocsExported,
				type: ESnackbarTypes.success,
			}));
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.apiDocsExportedError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: handleDownloadFolders,
		isLoading: isLoadingDownloadFolders,
	} = useMutation<IDownloadFoldersDTO>({
		mutationKey: [PORTAL_DOWNLOAD_FOLDER],
		mutationFn: downloadFolders,
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.apiDocsExported,
				type: ESnackbarTypes.success,
			}));
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.apiDocsExportedError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	return {
		createFolder,
		isCreatingFolder,
		handleCheckFolderName,
		isLoadingCheckFolderName,
		handleDeleteFolder,
		isLoadingDeleteFolder,
		handleRenameFolder,
		isLoadingRenameFolder,
		handleMoveFolder,
		isLoadingMoveFolder,
		handleDownloadFolder,
		isLoadingDownloadFolder,
		handleDownloadFolders,
		isLoadingDownloadFolders,
	};
}
